/**
 * Default Seasonal Events - Built-in event configurations
 *
 * These events are used as fallbacks when the backend is unavailable,
 * and provide themed content for major holidays throughout the year.
 */

import type { SeasonalEvent, ThemedInvader } from './types';

// Get current year for date calculations
const currentYear = new Date().getFullYear();

/**
 * Halloween-themed invader patterns
 */
const HALLOWEEN_INVADERS: ThemedInvader[] = [
  {
    // Ghost type (top row)
    key: 'invader_ghost',
    color: 0xffffff,
    points: 30,
    width: 32,
    height: 32,
    pattern1: [
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 0, 1, 1, 0, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 0, 1, 1, 1, 1, 0, 1],
      [1, 0, 1, 0, 0, 1, 0, 1],
    ],
    pattern2: [
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 0, 1, 1, 0, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 0, 1, 1, 0, 1, 1],
      [0, 1, 0, 1, 1, 0, 1, 0],
    ],
  },
  {
    // Pumpkin type (middle rows)
    key: 'invader_pumpkin',
    color: 0xff6600,
    points: 20,
    width: 44,
    height: 32,
    pattern1: [
      [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1],
      [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
      [1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1],
      [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
      [1, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
    ],
  },
  {
    // Bat type (bottom rows)
    key: 'invader_bat',
    color: 0x9933ff,
    points: 10,
    width: 48,
    height: 32,
    pattern1: [
      [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1],
      [1, 1, 0, 0, 1, 1, 1, 1, 0, 0, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 0, 0, 1, 1, 0, 0, 0],
      [0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0],
    ],
    pattern2: [
      [1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1],
      [1, 1, 1, 0, 1, 1, 1, 1, 0, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 1, 1, 1, 0, 1, 1, 0, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0],
    ],
  },
];

/**
 * Christmas-themed invader patterns
 */
const CHRISTMAS_INVADERS: ThemedInvader[] = [
  {
    // Snowflake type (top row)
    key: 'invader_snowflake',
    color: 0x87ceeb,
    points: 30,
    width: 32,
    height: 32,
    pattern1: [
      [0, 0, 0, 1, 1, 0, 0, 0],
      [1, 0, 0, 1, 1, 0, 0, 1],
      [0, 1, 0, 1, 1, 0, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 0, 1, 1, 0, 1, 0],
      [1, 0, 0, 1, 1, 0, 0, 1],
      [0, 0, 0, 1, 1, 0, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 1, 1, 0, 0, 0],
      [0, 1, 0, 1, 1, 0, 1, 0],
      [1, 0, 0, 1, 1, 0, 0, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 0, 0, 1, 1, 0, 0, 1],
      [0, 1, 0, 1, 1, 0, 1, 0],
      [0, 0, 0, 1, 1, 0, 0, 0],
    ],
  },
  {
    // Gift box type (middle rows)
    key: 'invader_gift',
    color: 0xff0000,
    points: 20,
    width: 44,
    height: 32,
    pattern1: [
      [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    ],
    pattern2: [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    ],
  },
  {
    // Christmas tree type (bottom rows)
    key: 'invader_tree',
    color: 0x228b22,
    points: 10,
    width: 48,
    height: 32,
    pattern1: [
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
  },
];

/**
 * Valentine's Day themed invader patterns
 */
const VALENTINES_INVADERS: ThemedInvader[] = [
  {
    // Heart type (top row)
    key: 'invader_heart',
    color: 0xff69b4,
    points: 30,
    width: 32,
    height: 32,
    pattern1: [
      [0, 1, 1, 0, 0, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0],
    ],
    pattern2: [
      [0, 1, 1, 0, 0, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 0, 0, 0],
    ],
  },
  {
    // Cupid type (middle rows)
    key: 'invader_cupid',
    color: 0xffb6c1,
    points: 20,
    width: 44,
    height: 32,
    pattern1: [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [1, 1, 0, 0, 1, 1, 1, 0, 0, 1, 1],
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
      [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0],
    ],
  },
  {
    // Rose type (bottom rows)
    key: 'invader_rose',
    color: 0xff1493,
    points: 10,
    width: 48,
    height: 32,
    pattern1: [
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
  },
];

/**
 * Summer-themed invader patterns
 */
const SUMMER_INVADERS: ThemedInvader[] = [
  {
    // Sun type (top row)
    key: 'invader_sun',
    color: 0xffd700,
    points: 30,
    width: 32,
    height: 32,
    pattern1: [
      [0, 0, 1, 0, 0, 1, 0, 0],
      [0, 0, 0, 1, 1, 0, 0, 0],
      [1, 0, 1, 1, 1, 1, 0, 1],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [1, 0, 1, 1, 1, 1, 0, 1],
      [0, 0, 0, 1, 1, 0, 0, 0],
      [0, 0, 1, 0, 0, 1, 0, 0],
    ],
    pattern2: [
      [0, 1, 0, 0, 0, 0, 1, 0],
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 1, 1, 1, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 0, 0],
      [0, 1, 0, 0, 0, 0, 1, 0],
    ],
  },
  {
    // Beach ball type (middle rows)
    key: 'invader_beachball',
    color: 0x00bfff,
    points: 20,
    width: 44,
    height: 32,
    pattern1: [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
      [1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
    ],
    pattern2: [
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 0],
      [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
      [0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
    ],
  },
  {
    // Palm tree type (bottom rows)
    key: 'invader_palm',
    color: 0x32cd32,
    points: 10,
    width: 48,
    height: 32,
    pattern1: [
      [0, 1, 1, 0, 0, 0, 0, 0, 0, 1, 1, 0],
      [1, 1, 0, 0, 0, 1, 1, 0, 0, 0, 1, 1],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
    pattern2: [
      [1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1],
      [0, 1, 1, 0, 0, 1, 1, 0, 0, 1, 1, 0],
      [0, 0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
      [0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0],
    ],
  },
];

/**
 * Default seasonal events with themed content
 */
export const DEFAULT_EVENTS: SeasonalEvent[] = [
  // Halloween Event (October 15 - November 5)
  {
    id: 'halloween-2024',
    name: 'Spooky Invasion',
    description: 'Ghosts, pumpkins, and bats have invaded! Earn bonus points during this haunted event.',
    type: 'halloween',
    startDate: new Date(currentYear, 9, 15).getTime(), // October 15
    endDate: new Date(currentYear, 10, 5, 23, 59, 59).getTime(), // November 5 end of day
    isActive: true,
    icon: '🎃',
    theme: {
      id: 'halloween',
      name: 'Spooky Night',
      backgroundColor: 0x1a0a2e,
      playerColor: 0x00ff00,
      playerBulletColor: 0x00ff00,
      alienBulletColor: 0xff6600,
      uiPrimaryColor: '#ff6600',
      invaderTypes: HALLOWEEN_INVADERS,
    },
    soundPack: {
      id: 'halloween-sounds',
      name: 'Spooky Sounds',
      marchFrequencies: [65.41, 55, 49, 43.65], // Lower, more ominous
      victoryNotes: [392, 493.88, 587.33, 783.99], // Minor key victory
    },
    bonusMultiplier: {
      scoreMultiplier: 1.5,
      invaderBonuses: {
        invader_ghost: 2.0, // Double points for ghosts
      },
      description: '50% bonus points! Double points for ghosts!',
    },
    achievements: [
      {
        id: 'halloween-first-game',
        name: 'Trick or Treat',
        description: 'Play your first game during the Halloween event',
        icon: '🍬',
        gamesRequired: 1,
      },
      {
        id: 'halloween-ghost-hunter',
        name: 'Ghost Hunter',
        description: 'Destroy 100 ghosts during Halloween',
        icon: '👻',
        aliensRequired: 100,
      },
      {
        id: 'halloween-high-score',
        name: 'Pumpkin King',
        description: 'Score 5000 points in a single game during Halloween',
        icon: '👑',
        pointsRequired: 5000,
      },
    ],
  },

  // Christmas Event (December 10 - January 5)
  {
    id: 'christmas-2024',
    name: 'Holiday Invasion',
    description: 'Snowflakes, gifts, and Christmas trees bring festive fun! Extra bonus points all season!',
    type: 'christmas',
    startDate: new Date(currentYear, 11, 10).getTime(), // December 10
    endDate: new Date(currentYear + 1, 0, 5, 23, 59, 59).getTime(), // January 5 end of day
    isActive: true,
    icon: '🎄',
    theme: {
      id: 'christmas',
      name: 'Winter Wonderland',
      backgroundColor: 0x0a1628,
      playerColor: 0x00ff00,
      playerBulletColor: 0xffd700,
      alienBulletColor: 0xff0000,
      uiPrimaryColor: '#ff0000',
      invaderTypes: CHRISTMAS_INVADERS,
    },
    soundPack: {
      id: 'christmas-sounds',
      name: 'Jingle Sounds',
      marchFrequencies: [523.25, 587.33, 659.25, 783.99], // Higher, more cheerful
      victoryNotes: [523.25, 659.25, 783.99, 1046.5], // Major key victory
    },
    bonusMultiplier: {
      scoreMultiplier: 2.0,
      invaderBonuses: {
        invader_gift: 3.0, // Triple points for gifts
      },
      description: 'Double points! Triple points for gift boxes!',
    },
    achievements: [
      {
        id: 'christmas-first-game',
        name: 'Holiday Cheer',
        description: 'Play your first game during the Christmas event',
        icon: '🎁',
        gamesRequired: 1,
      },
      {
        id: 'christmas-gift-collector',
        name: 'Gift Collector',
        description: 'Destroy 50 gift boxes during Christmas',
        icon: '🎀',
        aliensRequired: 50,
      },
      {
        id: 'christmas-high-score',
        name: 'Holiday Hero',
        description: 'Score 10000 points in a single game during Christmas',
        icon: '⭐',
        pointsRequired: 10000,
      },
    ],
  },

  // Valentine's Day Event (February 7 - February 21)
  {
    id: 'valentines-2025',
    name: 'Love Attack',
    description: 'Hearts, cupids, and roses invade! Spread the love with bonus points!',
    type: 'valentines',
    startDate: new Date(currentYear, 1, 7).getTime(), // February 7
    endDate: new Date(currentYear, 1, 21, 23, 59, 59).getTime(), // February 21 end of day
    isActive: true,
    icon: '💕',
    theme: {
      id: 'valentines',
      name: 'Love Is In The Air',
      backgroundColor: 0x1a0a14,
      playerColor: 0xff69b4,
      playerBulletColor: 0xff69b4,
      alienBulletColor: 0xff1493,
      uiPrimaryColor: '#ff69b4',
      invaderTypes: VALENTINES_INVADERS,
    },
    soundPack: {
      id: 'valentines-sounds',
      name: 'Lovely Sounds',
      marchFrequencies: [392, 349.23, 329.63, 293.66], // Soft, romantic
      victoryNotes: [261.63, 329.63, 392, 523.25], // Sweet victory
    },
    bonusMultiplier: {
      scoreMultiplier: 1.25,
      invaderBonuses: {
        invader_heart: 2.5, // Extra love for hearts
      },
      description: '25% bonus points! 2.5x points for hearts!',
    },
    achievements: [
      {
        id: 'valentines-first-game',
        name: 'First Love',
        description: 'Play your first game during Valentine\'s event',
        icon: '💌',
        gamesRequired: 1,
      },
      {
        id: 'valentines-heartbreaker',
        name: 'Heartbreaker',
        description: 'Destroy 75 hearts during Valentine\'s',
        icon: '💔',
        aliensRequired: 75,
      },
      {
        id: 'valentines-high-score',
        name: 'Cupid\'s Champion',
        description: 'Score 3000 points in a single game during Valentine\'s',
        icon: '💘',
        pointsRequired: 3000,
      },
    ],
  },

  // Summer Event (June 15 - August 31)
  {
    id: 'summer-2025',
    name: 'Beach Blast',
    description: 'Sun, beach balls, and palm trees! Enjoy summer vibes with bonus points!',
    type: 'summer',
    startDate: new Date(currentYear, 5, 15).getTime(), // June 15
    endDate: new Date(currentYear, 7, 31, 23, 59, 59).getTime(), // August 31 end of day
    isActive: true,
    icon: '☀️',
    theme: {
      id: 'summer',
      name: 'Beach Party',
      backgroundColor: 0x001a33,
      playerColor: 0x00ff00,
      playerBulletColor: 0x00ffff,
      alienBulletColor: 0xff4500,
      uiPrimaryColor: '#00bfff',
      invaderTypes: SUMMER_INVADERS,
    },
    soundPack: {
      id: 'summer-sounds',
      name: 'Beach Sounds',
      marchFrequencies: [261.63, 293.66, 329.63, 349.23], // Upbeat, tropical
      victoryNotes: [392, 440, 493.88, 523.25], // Bright victory
    },
    bonusMultiplier: {
      scoreMultiplier: 1.25,
      invaderBonuses: {
        invader_sun: 2.0, // Double points for suns
      },
      description: '25% bonus points! Double points for suns!',
    },
    achievements: [
      {
        id: 'summer-first-game',
        name: 'Beach Day',
        description: 'Play your first game during the Summer event',
        icon: '🏖️',
        gamesRequired: 1,
      },
      {
        id: 'summer-sunbather',
        name: 'Sunbather',
        description: 'Destroy 100 suns during Summer',
        icon: '🌞',
        aliensRequired: 100,
      },
      {
        id: 'summer-high-score',
        name: 'Beach Champion',
        description: 'Score 4000 points in a single game during Summer',
        icon: '🏆',
        pointsRequired: 4000,
      },
    ],
  },
];
